import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { authService } from "../api/services/authService";
import {
  LoginRequestData,
  ForgotPasswordRequestData,
  VerifyOtpRequestData,
  ResetPasswordRequestData,
} from "../schemas/auth/requestData";
import { User } from "../types/auth";
import {
  getPostLoginRoute,
  isBrandRole,
  shouldRenderBrandPortal,
} from "../config/portal";

interface AuthContextType {
  login: {
    loading: boolean;
    mutate: (data: LoginRequestData) => void;
  };
  logout: () => void;
  forgotPassword: (data: ForgotPasswordRequestData) => void;
  verifyOtp: (data: VerifyOtpRequestData) => void;
  resetPassword: (data: ResetPasswordRequestData) => void;
  profile: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export const useAuth = (): AuthContextType => {
  const [profile, setProfile] = useState<User | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const storedProfile = localStorage.getItem("profile");
    if (storedProfile) {
      try {
        setProfile(JSON.parse(storedProfile));
      } catch (error) {
        // Invalid profile data, remove it
        localStorage.removeItem("profile");
      }
    }
  }, []);

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (authPayload) => {
      localStorage.setItem("token", authPayload.accessToken);
      localStorage.setItem("refreshToken", authPayload.refreshToken);
      localStorage.setItem("profile", JSON.stringify(authPayload.user));
      setProfile(authPayload.user);

      const route = getPostLoginRoute(authPayload.user?.role);
      toast.success("Login successful");

      // Which route tree renders is decided once, above the router, from the
      // stored profile. A brand signing in on the admin host therefore needs a
      // real page load for that decision to be re-made — a client-side
      // navigate() would leave them inside the admin shell.
      if (isBrandRole(authPayload.user?.role) && !shouldRenderBrandPortal()) {
        window.location.assign(route);
        return;
      }

      // Brand accounts have no dashboard permission, so a hardcoded
      // /dashboard would bounce them straight to /unauthorized.
      navigate(route);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Login failed");
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: authService.forgotPassword,
    onSuccess: (response) => {
      toast.success(response.message || "OTP sent to your email");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to send OTP");
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: authService.verifyOtp,
    onSuccess: (response) => {
      toast.success(response.message || "OTP verified successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to verify OTP");
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: authService.resetPassword,
    onSuccess: () => {
      toast.success("Password reset successful");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Password reset failed");
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("profile");
      setProfile(null);
      queryClient.clear();
      navigate("/login");
      toast.success("Logged out successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Logout failed");
    },
  });

  const isAuthenticated = !!localStorage.getItem("token");

  return {
    login: {
      loading: loginMutation.isPending,
      mutate: loginMutation.mutate,
    },
    logout: logoutMutation.mutate,
    forgotPassword: forgotPasswordMutation.mutate,
    verifyOtp: verifyOtpMutation.mutate,
    resetPassword: resetPasswordMutation.mutate,
    profile,
    isLoading: false,
    isAuthenticated,
  };
};
